import React, { useEffect, useState } from 'react';
import { Button } from 'antd';
import { BOOK_DETAIL_API_BASE, bookDetailApiGet, bookDetailApiPost } from '../bookDetail/BookDetailPage.api';

type Rule = { id: string; scope: string; kind: string; source: string; target: string; disposition: string; effectiveTarget?: string };
type Contract = { revision: string; capabilities?: { spellingIntent: boolean }; rules: Rule[]; effective: { never: string[]; always: string[] }; overlaps: { disposition: string }[] };
const labels: Record<string, string> = {
  effective: 'Applies', duplicate: 'Also saved in a later scope', shadowed: 'Overridden by a later scope',
  always_override: 'Overridden by Always Translate', legacy_identity_override: 'Disabled by a legacy identity rule',
  legacy_identity: 'Legacy identity rule: contextual translation', legacy_case_only: 'Legacy capitalization rule: inactive',
  boundary_excluded: 'Inactive: target conflicts with the source boundary', conflicting: 'Conflicting targets: review required',
  invalid: 'Invalid rule', excluded: 'Inactive',
  never_overlap_override: 'Never Translate takes priority inside this phrase',
};

export default function ResolvedGlossaryPanel({ bookId, sourceLang, targetLang, refreshKey, onContractLoaded }: {
  bookId: string; sourceLang: string; targetLang: string; refreshKey: unknown;
  onContractLoaded?: (revision: string, spellingEnabled: boolean) => void;
}) {
  const key = JSON.stringify([bookId, sourceLang, targetLang]);
  const [state, setState] = useState<{ key: string; contract?: Contract; error?: string }>({ key });
  const [retry, setRetry] = useState(0);
  const [decisionError, setDecisionError] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let current = true;
    setState({ key });
    onContractLoaded?.('', false);
    void bookDetailApiGet<Contract>(`${BOOK_DETAIL_API_BASE}/books/${bookId}/glossary-contract`, { params: { source_lang: sourceLang, target_lang: targetLang } })
      .then(response => { if (current) { setState({ key, contract: response.data }); onContractLoaded?.(response.data.revision, !!response.data.capabilities?.spellingIntent); } })
      .catch(() => { if (current) { setState({ key, error: 'The effective glossary could not be loaded. Saved rows below do not confirm which rules apply.' }); onContractLoaded?.('', false); } });
    return () => { current = false; };
  }, [key, bookId, sourceLang, targetLang, refreshKey, retry, onContractLoaded]);
  const visible = state.key === key ? state : { key };
  if (visible.error) return <div role="alert">{visible.error} <Button onClick={() => setRetry(n => n + 1)}>Retry effective glossary</Button></div>;
  if (!visible.contract) return <p role="status">Loading effective glossary…</p>;
  const contract = visible.contract;
  const count = contract.effective.never.length + contract.effective.always.length;
  const activateSpelling = async (row: Rule) => {
    setDecisionError('');
    setSaving(true);
    try {
      await bookDetailApiPost(`${BOOK_DETAIL_API_BASE}/books/${bookId}/glossary-spelling-intent`, {
        source_lang: sourceLang, target_lang: targetLang, source: row.source, target: row.target,
        scope: row.scope, expected_revision: contract.revision,
      });
      setRetry(value => value + 1);
    } catch (error: any) {
      setDecisionError(typeof error?.response?.data?.detail === 'string' ? error.response.data.detail : 'The spelling decision could not be saved. Refresh and try again.');
    } finally {
      setSaving(false);
    }
  };
  return <details>
    <summary>{contract.rules.length} saved rules · {count} effective rules across Book, Series and Account</summary>
    <p>Account rules override Series rules, which override Book rules for the same source. Always Translate allows grammatical inflection; Never Translate remains exact. These counts describe configuration, not compliance of the translated text.</p>
    {decisionError && <p role="alert">{decisionError}</p>}
    {contract.overlaps.some(row => row.disposition === 'needs_decision') && <p role="alert">Some protected phrases overlap translation rules. Review those decisions before translating.</p>}
    <ul>{contract.rules.map((row, index) => <li key={`${row.id}:${index}`}>
      {row.scope}: {row.source}{row.kind === 'always' ? ` → ${row.target}` : ''} — {labels[row.disposition] || row.disposition}
      {row.effectiveTarget ? ` (${row.effectiveTarget})` : ''}
      {row.disposition === 'legacy_case_only' && contract.capabilities?.spellingIntent && <Button disabled={saving} onClick={() => void activateSpelling(row)}>Use this spelling</Button>}
    </li>)}</ul>
  </details>;
}
